/**
 * DModal: the old dialog.js modal builder, rebuilt on Bootstrap 5's Modal.
 * Same method names: createModal(opt), createButton(text, cb, cssClass, append), load(url, cb), html(html), show(), hide().
 * `modal` is now the modal Element (was a jQuery object).
 */
import { Modal } from 'bootstrap';
import { loadInto } from '../lib/http';
import { setHTML } from '../lib/dom';

const BASE_Z = 1055; // Bootstrap 5 --bs-modal-zindex

function el(html) {
    const tpl = document.createElement('template');
    tpl.innerHTML = html.trim();
    return tpl.content.firstElementChild;
}

export class DModal {
    constructor() {
        this.id = String(Math.floor(Math.random() * 9867654321 * 999));
        this.modal = null;
        this.instance = null;
    }

    createModal(opt = {}) {
        if (opt.id != null) this.id = opt.id;
        const modal = el(`<div class="modal" id="${this.id}" role="dialog" tabindex="-1"></div>`);
        if (opt.extra != null) {
            Object.entries(opt.extra).forEach(([key, value]) => modal.setAttribute(`data-${key}`, value));
        }
        const closeWhite = /text-white/.test(opt.headerClass || '') ? ' text-white' : '';
        modal.innerHTML = `
            <div class="modal-dialog ${opt.dialogClass ?? ''}" id="${this.id}-dialog">
                <div class="modal-content ${opt.contentClass ?? ''}">
                    <div class="modal-header ${opt.headerClass ?? ''}">
                        ${opt.header ? `<div class="modal-title text-white"><h5>${opt.header}</h5></div>` : ''}
                        <button type="button" class="close${closeWhite}" data-bs-dismiss="modal" aria-label="Close">&times;</button>
                    </div>
                    <div class="modal-body ${opt.bodyClass ?? ''}" id="${this.id}-body"></div>
                    <div class="modal-footer justify-content-center ${opt.footerClass ?? ''}" style="text-align:center !important;"></div>
                </div>
            </div>`;
        document.body.appendChild(modal);
        this.modal = modal;
        this.instance = new Modal(modal, { show: false });
        modal.addEventListener('hidden.bs.modal', () => {
            this.instance.dispose();
            modal.remove();
        });
        return modal;
    }

    createButton(text, callback, cssClass, appendOrder) {
        const button = document.createElement('button');
        button.type = 'button';
        button.style.width = '80px';
        button.className = cssClass == null ? 'btn btn-secondary' : cssClass;
        button.innerHTML = text;
        button.addEventListener('click', () => {
            if (callback != null) callback();
            this.hide();
        });
        const footer = this.modal.querySelector('.modal-footer');
        if (appendOrder == null || appendOrder) footer.appendChild(button);
        else footer.prepend(button);
        return button;
    }

    load(url, callback) {
        return loadInto(this.modal.querySelector('.modal-body'), url).then(() => callback && callback());
    }

    html(html) {
        setHTML(this.modal.querySelector('.modal-body'), html);
    }

    show() {
        this.instance.show();
        // Stack above any modal already open (the old z-index juggling).
        const open = document.querySelectorAll('.modal-backdrop').length;
        const z = BASE_Z + 10 * Math.max(open - 1, 0);
        const backdrops = document.querySelectorAll('.modal-backdrop');
        const backdrop = backdrops[backdrops.length - 1];
        if (backdrop) backdrop.style.zIndex = String(z - 1);
        this.modal.style.zIndex = String(z);
    }

    hide() {
        this.instance?.hide();
    }
}

/** Hide the modal element(s) matching a selector or element, like `$(x).modal('hide')`. */
export function hideModal(target) {
    const els = typeof target === 'string' ? document.querySelectorAll(target) : [target];
    els.forEach((m) => m && Modal.getOrCreateInstance(m).hide());
}
